import React from 'react';
import ReactDOM from 'react-dom/client';
import { LiveChatWidget } from '@livechat/widget-react';

const initTrustPilotWidgets = () => {
	const trustPilotWidgets = document.querySelectorAll('.js-trustpilot-widget');
	if (trustPilotWidgets.length === 0) return;

	if (window.Trustpilot) {
		trustPilotWidgets.forEach((trustbox) => {
			window.Trustpilot.loadFromElement(trustbox);
		});
		return;
	}

	// Script not ready yet — poll until it is (max 3s)
	let attempts = 0;
	const interval = setInterval(() => {
		attempts++;
		if (window.Trustpilot) {
			clearInterval(interval);
			trustPilotWidgets.forEach((trustbox) => {
				window.Trustpilot.loadFromElement(trustbox);
			});
		} else if (attempts >= 30) {
			clearInterval(interval);
		}
	}, 100);
};

const HIDDEN_SEGMENTS = ['referral', 'friend'];

const isHiddenPath = (pathname) => {
	const segments = pathname.split('/').filter(Boolean);
	return HIDDEN_SEGMENTS.some(s => segments.includes(s));
};

const syncLiveChatVisibility = () => {
	if (typeof window === 'undefined' || typeof window.LiveChatWidget === 'undefined') return;
	if (isHiddenPath(window.location.pathname)) {
		window.LiveChatWidget.call('hide');
	} else {
		window.LiveChatWidget.call('minimize');
	}
};

const initLiveChat = () => {
	const currentPath = window.location.pathname;
	if (isHiddenPath(currentPath)) return;

	const now = new Date();
	const etNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
	const day = etNow.getDay();
	const hour = etNow.getHours();

	// LiveChat during business hours Monday to Friday 8am to 8pm
	if (day >= 1 && day <= 5 && hour >= 8 && hour < 20) {
		if (window.LiveChatWidget) return;

		window.__lc = window.__lc || {};
		window.__lc.license = 11976798;
		window.__lc.integration_name = 'manual_channels';
		window.__lc.product_name = 'livechat';
		window.__lc.chat_between_groups = false;

		// Official LiveChat async stub: exposes window.LiveChatWidget immediately and
		// queues API calls (e.g. call('maximize')) until tracking.js finishes loading
		(function (n, t, c) {
			function i(n) {
				return e._h ? e._h.apply(null, n) : e._q.push(n);
			}
			var e = {
				_q: [],
				_h: null,
				_v: '2.0',
				on: function () {
					i(['on', c.call(arguments)]);
				},
				once: function () {
					i(['once', c.call(arguments)]);
				},
				off: function () {
					i(['off', c.call(arguments)]);
				},
				get: function () {
					if (!e._h) throw new Error("[LiveChatWidget] You can't use getters before load.");
					return i(['get', c.call(arguments)]);
				},
				call: function () {
					i(['call', c.call(arguments)]);
				},
				init: function () {
					var n = t.createElement('script');
					n.async = !0;
					n.type = 'text/javascript';
					n.src = 'https://cdn.livechatinc.com/tracking.js';
					t.head.appendChild(n);
				}
			};
			!n.__lc.asyncInit && e.init();
			n.LiveChatWidget = n.LiveChatWidget || e;
		})(window, document, [].slice);
	}
};

export const onRouteUpdate = () => {
	initTrustPilotWidgets();
	initLiveChat();
	syncLiveChatVisibility();
};

export const shouldUpdateScroll = ({ routerProps: { location } }) => {
	if (location.hash) {
		return location.hash;
	}
	window.scrollTo(0, 0);
	return false;
};

export const onInitialClientRender = () => {
	initTrustPilotWidgets();
};

// React strips component stacks from minified hydration errors (#418/#423/#425),
// so log them to TrackJS ourselves to see which component actually mismatched
export const replaceHydrateFunction = () => (element, container) => {
	const onRecoverableError = (error, errorInfo) => {
		if (window.TrackJS) {
			if (errorInfo && errorInfo.componentStack) {
				window.TrackJS.console.log('Hydration component stack:', errorInfo.componentStack);
			}
			window.TrackJS.track(error);
		} else {
			console.error(error, errorInfo && errorInfo.componentStack);
		}
	};

	// Client-only pages have an empty body, so render instead of hydrating
	// (mirrors Gatsby's default renderer in cache-dir/production-app.js)
	const focusEl = document.getElementById('gatsby-focus-wrapper');
	if (focusEl && focusEl.children.length) {
		return ReactDOM.hydrateRoot(container, element, { onRecoverableError });
	}

	const root = ReactDOM.createRoot(container, { onRecoverableError });
	root.render(element);
	return root;
};

export const wrapPageElement = ({ element }) => {
	return (
		<React.Fragment>
			{element}
			<LiveChatWidget
				license="11976798"
				visibility="minimized"
			/>
		</React.Fragment>
	);
};

// OneTrust — suppress native banner, handle returning visitors
var _hcPrefsOpen = false;

window._otWrapper = function() {
	// Always suppress OT native banner
	if (!document.getElementById('hc-cookie-styles')) {
		var style = document.createElement('style');
		style.id = 'hc-cookie-styles';
		style.textContent = '#onetrust-banner-sdk { display: none !important; }';
		document.head.appendChild(style);
	}

	// Returning visitor — OT alert box was previously closed
	if (document.cookie.indexOf('OptanonAlertBoxClosed=') !== -1) {
		localStorage.setItem('hc-cookie-consent', '1');
		if (window._hideCookieBanner) window._hideCookieBanner();
		return;
	}

	// User just saved from OT native preference center
	if (_hcPrefsOpen) {
		_hcPrefsOpen = false;
		if (window._hideCookieBanner) window._hideCookieBanner();
	}
};

// OpenPanel now lives entirely in static/opTracking.js, loaded from gatsby-ssr.js so the
// homepage and the static LPs share one implementation.
